import { z } from "zod";
import {
    AccountingLedgerBasis,
    BOTH_LEDGER_BASIS,
} from "@common/types/domains/accounting/ledger";
import {
    ManualJournalRecurringRule,
    ManualJournalRecurringUpdate,
} from "@common/types/domains/accounting/manualJournalRecurringRule";
import { zQueryBoolean } from "@common/schemas/query-boolean.schema";
import {
    ManualJournalRecurringRuleSchema,
    ManualJournalRecurringRuleUpdateSchema,
    ManualJournalRecurringRuleWriteSchema,
} from "./manualJournalRecurringRule";

interface DebitCreditLine {
    debitAmount: number | null;
    creditAmount: number | null;
}

function hasExactlyOneAmount(line: DebitCreditLine): boolean {
    return (line.debitAmount === null) !== (line.creditAmount === null);
}

export const JOURNAL_LINE_AMOUNT_MUST_BE_WHOLE_CENTS =
    "Journal line amounts must be whole cents";

function isWholeCents(amount: number): boolean {
    const scaled = amount * 100;
    if (!Number.isFinite(scaled)) {
        return false;
    }
    const cents = Math.round(scaled);
    // A positive line amount must round to at least one cent, otherwise
    // sub-penny values (e.g. 0.000000001) slip past the tolerance and later
    // round to zero at the two-decimal line column.
    return cents >= 1 && Math.abs(scaled - cents) < 1e-6;
}

const positiveCentAmountSchema = z.number().positive().refine(isWholeCents, {
    message: JOURNAL_LINE_AMOUNT_MUST_BE_WHOLE_CENTS,
});

function areDebitAndCreditSumsEqual(
    lines: readonly DebitCreditLine[],
): boolean {
    let debitAmount = 0;
    let creditAmount = 0;
    for (const line of lines) {
        debitAmount += line.debitAmount ?? 0;
        creditAmount += line.creditAmount ?? 0;
    }
    return debitAmount.toFixed(2) === creditAmount.toFixed(2);
}

export function validateDebitAndCreditSums(
    data: { lines: readonly DebitCreditLine[] },
    ctx: z.RefinementCtx,
): void {
    if (areDebitAndCreditSumsEqual(data.lines)) {
        return;
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Debit and credit sums must be equal",
        path: ["lines"],
    });
}

export const REVERSAL_DATE_BEFORE_ENTRY_DATE_MESSAGE =
    "Reversal date must be on or after the journal entry date";

interface ReversalDateCheck {
    date: string;
    reversedAt?: string | null;
}

export function validateReversalDate(
    data: ReversalDateCheck,
    ctx: z.RefinementCtx,
): void {
    if (!data.reversedAt || data.reversedAt >= data.date) {
        return;
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REVERSAL_DATE_BEFORE_ENTRY_DATE_MESSAGE,
        path: ["reversedAt"],
    });
}

interface BulkCreateRecurringRuleCheck {
    recurringRule?: ManualJournalRecurringRule | null;
}

export const BULK_CREATE_RECURRING_RULE_UNSUPPORTED_MESSAGE =
    "recurringRule is not supported on bulk create. Create a single manual journal with recurringRule instead.";

export function rejectRecurringRuleOnBulkCreate(
    data: BulkCreateRecurringRuleCheck,
    ctx: z.RefinementCtx,
): void {
    if (data.recurringRule == null) {
        return;
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: BULK_CREATE_RECURRING_RULE_UNSUPPORTED_MESSAGE,
        path: ["recurringRule"],
    });
}

export enum JournalEntrySourceType {
    Transaction = "transaction",
    ManualJournalEntry = "manual_journal_entry",
    OpeningBalance = "opening_balance",
    Loan = "loan",
    Bill = "bill",
    Invoice = "invoice",
    Schedule = "schedule",
}

export enum JournalEntrySourceFilter {
    Transaction = "transaction",
    ManualJournalEntry = "manual_journal_entry",
    // Wire value predates the QBO/QBD split; kept as "quickbooks" so saved
    // views and URLs continue to parse. It now matches QBO entries only.
    QuickBooksOnline = "quickbooks",
    QuickBooksDesktop = "quickbooks-desktop",
    Xero = "xero",
    MigratedData = "migrated-data",
    OpeningBalance = "opening_balance",
    Loan = "loan",
    Bill = "bill",
    Invoice = "invoice",
    Recurring = "recurring",
}

export const JournalEntrySourceSchema = z.object({
    type: z.nativeEnum(JournalEntrySourceType),
    id: z.string().or(z.number()),
    origin: z.string().optional(),
});

export const JournalEntryLineSchema = z
    .object({
        debitAmount: z.number().nullable(),
        creditAmount: z.number().nullable(),
        accountCode: z.number(),
        description: z.string(),
        accountId: z.string(),
        lineOrder: z.number().optional(),
        classIds: z.array(z.string()).optional(),
        counterpartyId: z.string().uuid().nullable().optional(),
        excludeFrom1099: z.boolean().optional(),
        // Split families concatenate each part's 2-line JE under one
        // referenceNumber. Per-line `transactionId` lets the drawer map an
        // edited non-financial line back to the matching split part.
        transactionId: z.number().int().positive().nullable().optional(),
        // Optional attribution to a specific entity, currently written only by
        // consolidation eliminating entries (the line still posts to its
        // ledger's chart; this records which group member it relates to). Modeled
        // on the persisted line type so readers can discover it from the type
        // rather than relying on JSONB pass-through.
        entityId: z.number().int().positive().nullable().optional(),
    })
    .refine(hasExactlyOneAmount, {
        message:
            "One of debitAmount or creditAmount must be null, but not both",
        path: ["debitAmount", "creditAmount"],
    });

export const JournalEntrySchema = z.object({
    id: z.string(),
    workspaceId: z.string().uuid(),
    accountingLedgerId: z.string(),
    /** @deprecated Use `dateOnly` for the accounting calendar day. Legacy UTC timestamp; persist on writes only. */
    date: z.coerce.date(),
    dateOnly: z.string().nullable().optional(),
    memo: z.string().nullable(),
    lines: z.array(JournalEntryLineSchema),
    source: JournalEntrySourceSchema,
    financialDocumentIds: z.array(z.number()).optional(),
    reversedAt: z.coerce.date().nullable().optional(),
    reversingManualJournalEntryIds: z.array(z.string()).optional(),
    reversedManualJournalEntryId: z.string().nullable().optional(),
    recurringRuleId: z.string().nullable().optional(),
    recurringRule: ManualJournalRecurringRuleSchema.nullable().optional(),
    ledgerBasis: z.nativeEnum(AccountingLedgerBasis),
    // True when the entry concatenates lines from more than one transaction
    // (a split family sharing one `referenceNumber`).
    isCombinedSplitJournalEntry: z.boolean().optional(),
});

export const manualJournalEntryLineBodySchema = z
    .object({
        debitAmount: positiveCentAmountSchema.nullable(),
        creditAmount: positiveCentAmountSchema.nullable(),
        accountCode: z.number(),
        accountId: z.string(),
        description: z.string(),
        classIds: z.array(z.string()).optional(),
        counterpartyId: z.string().uuid().nullable().optional(),
        excludeFrom1099: z.boolean().optional(),
    })
    .refine(hasExactlyOneAmount, {
        message:
            "One of debitAmount or creditAmount must be null, but not both",
        path: ["debitAmount", "creditAmount"],
    });

export const manualJournalLedgerBasisBodySchema = z.union([
    z.nativeEnum(AccountingLedgerBasis),
    z.literal(BOTH_LEDGER_BASIS),
]);

export const getManualJournalEntriesQuerySchema = z.object({
    financialAccountId: z.coerce.number().int().optional(),
    excludeImported: zQueryBoolean().optional(),
    limit: z.coerce.number().int().positive().optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
    search: z.string().trim().min(1).optional(),
    dateFrom: z.string().date().optional(),
    dateTo: z.string().date().optional(),
});

export const manualJournalEntryBodyBaseSchema = z.object({
    date: z.string().date(),
    memo: z.string().nullable().optional(),
    lines: z.array(manualJournalEntryLineBodySchema).min(2).max(500),
    financialDocumentIds: z.array(z.number()).optional(),
    reversedAt: z.string().date().nullable().optional(),
    recurringRuleUpdateType: z
        .nativeEnum(ManualJournalRecurringUpdate)
        .nullable()
        .optional(),
    recurringRule: ManualJournalRecurringRuleWriteSchema.nullable().optional(),
});

const createManualJournalEntryObjectSchema =
    manualJournalEntryBodyBaseSchema.extend({
        ledgerBasis: manualJournalLedgerBasisBodySchema,
        ledgerId: z.string().uuid().optional(),
    });

export const createManualJournalEntryBodySchema =
    createManualJournalEntryObjectSchema
        .superRefine(validateDebitAndCreditSums)
        .superRefine(validateReversalDate);

export const createManualJournalEntryMcpBodySchema =
    createManualJournalEntryObjectSchema
        .extend({
            ledgerBasis:
                createManualJournalEntryObjectSchema.shape.ledgerBasis.optional(),
        })
        .strict()
        .superRefine(validateDebitAndCreditSums)
        .superRefine(validateReversalDate);

export const createManualJournalEntriesBodySchema = z.object({
    manualJournalEntries: z
        .array(
            createManualJournalEntryBodySchema.superRefine(
                rejectRecurringRuleOnBulkCreate,
            ),
        )
        .min(1)
        .max(50),
});

const updateManualJournalEntryObjectSchema =
    manualJournalEntryBodyBaseSchema.extend({
        ledgerBasis: z.nativeEnum(AccountingLedgerBasis),
        ledgerId: z.string().uuid().optional(),
        recurringRule:
            ManualJournalRecurringRuleUpdateSchema.nullable().optional(),
    });

export const updateManualJournalEntryBodySchema =
    updateManualJournalEntryObjectSchema
        .superRefine(validateDebitAndCreditSums)
        .superRefine(validateReversalDate);

export const updateManualJournalEntryMcpBodySchema =
    updateManualJournalEntryObjectSchema
        .extend({
            ledgerBasis:
                updateManualJournalEntryObjectSchema.shape.ledgerBasis.optional(),
        })
        .strict()
        .superRefine(validateDebitAndCreditSums)
        .superRefine(validateReversalDate);

export const deleteManualJournalEntryBodySchema = z.object({
    recurringRuleUpdateType: z
        .nativeEnum(ManualJournalRecurringUpdate)
        .nullable()
        .optional(),
    ledgerBasis: z.nativeEnum(AccountingLedgerBasis),
    ledgerId: z.string().uuid().optional(),
});

export const deleteManualJournalEntryMcpBodySchema =
    deleteManualJournalEntryBodySchema
        .extend({
            ledgerBasis:
                deleteManualJournalEntryBodySchema.shape.ledgerBasis.optional(),
        })
        .strict();

export const ManualJournalEntryResponseSchema = JournalEntrySchema.omit({
    source: true,
    ledgerBasis: true,
}).extend({
    importedJournalEntryId: z.string().nullable().optional(),
});

export const ManualJournalMutationResponseSchema =
    ManualJournalEntryResponseSchema.extend({
        journalEntries: z.array(
            z.object({
                debitAmount: z.number().nullable(),
                creditAmount: z.number().nullable(),
                date: z.coerce.date().or(z.string().date()),
                transactionId: z.number().nullable(),
                manualJournalEntryId: z.string().nullable(),
                loanId: z.string().nullable(),
                billId: z.string().nullable(),
                invoiceId: z.string().nullable(),
                accountId: z.string(),
                accountingLedgerId: z.string(),
                openingBalanceId: z.string().nullable(),
                referenceNumber: z.string(),
                description: z.string(),
                memo: z.string().nullable(),
                id: z.string(),
                splitAccountIds: z.array(z.string()),
                lineOrder: z.number(),
                counterpartyId: z.string().nullable(),
                excludeFrom1099: z.boolean().optional(),
            }),
        ),
    });

export const ManualJournalEntriesMutationResponseSchema = z.object({
    manualJournalEntries: z.array(ManualJournalMutationResponseSchema),
});

export const getManualJournalEntriesResponseSchema = z.object({
    manualJournalEntries: z.array(JournalEntrySchema),
    total: z.number().optional(),
});
